import {useDispatch, useSelector} from 'react-redux';

import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {createLogger} from '~/shared/logging';
import actions from '~/shared/store/actions';
import {selectUserData} from '~/shared/store/selectors';
import {trackEvent} from '~/shared/services/analytics';
import {brandName} from '~/shared/config';
import {useIsSafari} from '~/shared/hooks/deviceInfo';

const logger = createLogger('googlepay');

type UseGooglePayArgs = {
  totalToCharge: number;
  isCheckoutInitialized: boolean;
  handleCheckoutSubmit: ({shouldSkipAgeRestriction}: {shouldSkipAgeRestriction: boolean}) => Promise<boolean | undefined>;
};

const useGooglePay = ({totalToCharge, isCheckoutInitialized, handleCheckoutSubmit}: UseGooglePayArgs) => {
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData);
  const isSafari = useIsSafari();

  const showGooglePayButton = isCheckoutInitialized && !isSafari;

  const paymentRequest: google.payments.api.PaymentDataRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX'],
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'telerompay',
            gatewayMerchantId: 'Telerom Computer solutions ltd',
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: 'BCR2DN6T57A63RK7',
      merchantName: brandName,
    },
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPriceLabel: brandName,
      totalPrice: totalToCharge.toFixed(2),
      currencyCode: 'ILS',
      countryCode: 'IL',
    },
  };

  const onGooglePayButtonClick = async (paymentData: google.payments.api.PaymentData) => {
    trackEvent('payWithGoogle_click', {basketValue: totalToCharge});
    try {
      const jsonString = JSON.stringify(paymentData);
      const encodedPaymentToken = btoa(
        new TextEncoder().encode(jsonString).reduce((acc, byte) => acc + String.fromCharCode(byte), ''),
      );
      const googlePayPayment = {
        paymentMethod: PaymentMethodTypes.GOOGLE_PAY,
        googlePayPaymentData: {paymentData: encodedPaymentToken, methodType: paymentData.paymentMethodData?.type, cardDetails: paymentData.paymentMethodData?.info?.cardDetails, cardNetwork: paymentData.paymentMethodData?.info?.cardNetwork},
        userId: userData?.userId || 0,
        sum: totalToCharge,
        assigned: true,
        isTenbisCredit: false,
        remarks: '',
        userName: `${userData?.firstName} ${userData?.lastName}`,
        prepaidBalance: null,
      };
      
      dispatch(actions.setPayments(googlePayPayment));
      await handleCheckoutSubmit({shouldSkipAgeRestriction: false});
    } catch (error) {
      logger.error('fail to complete payment', error);
    }
  };

  return {
    showGooglePayButton,
    paymentRequest,
    onGooglePayButtonClick,
  };
};

export default useGooglePay;
